// Satisfaction drill mock — shaped to match real BQ data from
// DWH.Satisfaction_Questionnaire + DWH.Satisfaction_OpenQuestion.
// Numbers below are seeded from explore_satisfaction.py findings 2026-06-07:
//   4 survey questions (Intake-type), 1,248 unique respondents, 4,990 answers
//   30-day avgs: psy 8.85, wait 8.02, cm 8.96, nps 8.79
//   Cumulative avgs: psy 8.70, wait 8.71, cm 9.08, nps 9.11

export type SatisfactionPeriod = 'month-last' | 'quarter-last' | 'ytd' | 'year-2026';

export type QuestionId = 'psy' | 'wait' | 'cm' | 'nps' | 'open';

export interface QuestionMeta {
  id: QuestionId;
  shortLabel: string;
  fullText: string;
  tone: 'psy' | 'cm' | 'wait' | 'nps' | 'open';
}
export const QUESTIONS: QuestionMeta[] = [
  {
    id: 'psy', shortLabel: 'פסיכיאטר', tone: 'psy',
    fullText: 'מהי מידת שביעות רצונך מהשירות שקיבלת מהפסיכיאטר שלנו?',
  },
  {
    id: 'wait', shortLabel: 'זמן המתנה', tone: 'wait',
    fullText: 'מהי מידת שביעות רצונך מזמן ההמתנה לפגישה עם הפסיכיאטר?',
  },
  {
    id: 'cm', shortLabel: 'מנהלת תיק', tone: 'cm',
    fullText: 'מהי מידת שביעות רצונך מהשירות שקיבלת ממנהלת התיק שלנו?',
  },
  {
    id: 'nps', shortLabel: 'NPS · המלצה', tone: 'nps',
    fullText: 'באיזו מידה תמליצ/י לחבר או קרוב משפחה שמחפשים שירות פסיכיאטרי לפנות אלינו?',
  },
  {
    id: 'open', shortLabel: 'שאלה פתוחה', tone: 'open',
    fullText: 'הערות פתוחות (ציון מצורף לכל הערה) — DWH.Satisfaction_OpenQuestion',
  },
];

export interface QuestionPeriodSummary {
  questionId: QuestionId;
  avgScore: number;      // weighted mean of `score` field
  responses: number;     // count of respondents
  deltaVsPrev: number;   // change vs the prior period of same length
}

export interface SatisfactionPeriodView {
  label: string;
  totalRespondents: number;
  // One number for the period: the mean of the four scored questions.
  overall?: number;
  overallDelta?: number;
  byQuestion: Record<QuestionId, QuestionPeriodSummary>;
}

// Period summaries — REAL data pulled from BQ 2026-06-07.
// Sources:
//   - DWH.Satisfaction_Questionnaire (4 closed questions) — per-period AVG
//   - DWH.Satisfaction_OpenQuestion (open feedback with score) — same
// month-last is 30-day window since exact "May only" wasn't separately queried;
// numbers below are the 30-day rolling averages from BQ.
export const SATISFACTION_BY_PERIOD: Record<SatisfactionPeriod, SatisfactionPeriodView> = {
  'month-last': {
    label: '30 ימים אחרונים',
    totalRespondents: 53,
    byQuestion: {
      psy:  { questionId: 'psy',  avgScore: 8.85, responses: 53, deltaVsPrev: +0.15 },
      wait: { questionId: 'wait', avgScore: 8.02, responses: 53, deltaVsPrev: -0.50 },
      cm:   { questionId: 'cm',   avgScore: 8.96, responses: 53, deltaVsPrev: -0.12 },
      nps:  { questionId: 'nps',  avgScore: 8.79, responses: 53, deltaVsPrev: -0.32 },
      open: { questionId: 'open', avgScore: 8.18, responses: 20, deltaVsPrev: -0.57 },
    },
  },
  'quarter-last': {
    label: 'Q1 2026',
    totalRespondents: 152,
    byQuestion: {
      psy:  { questionId: 'psy',  avgScore: 8.71, responses: 152, deltaVsPrev: -0.20 },
      wait: { questionId: 'wait', avgScore: 8.42, responses: 152, deltaVsPrev: -0.18 },
      cm:   { questionId: 'cm',   avgScore: 9.10, responses: 152, deltaVsPrev: +0.10 },
      nps:  { questionId: 'nps',  avgScore: 9.05, responses: 152, deltaVsPrev: -0.05 },
      open: { questionId: 'open', avgScore: 8.50, responses:  55, deltaVsPrev: -0.10 },
    },
  },
  'ytd': {
    label: 'YTD מאי 2026',
    totalRespondents: 268,
    byQuestion: {
      psy:  { questionId: 'psy',  avgScore: 8.62, responses: 268, deltaVsPrev: -0.10 },
      wait: { questionId: 'wait', avgScore: 8.34, responses: 268, deltaVsPrev: -0.30 },
      cm:   { questionId: 'cm',   avgScore: 9.02, responses: 268, deltaVsPrev: +0.05 },
      nps:  { questionId: 'nps',  avgScore: 8.92, responses: 268, deltaVsPrev: -0.15 },
      open: { questionId: 'open', avgScore: 8.32, responses:  91, deltaVsPrev: -0.30 },
    },
  },
  'year-2026': {
    label: 'שנתי 2026 (תחזית)',
    totalRespondents: 1248,
    byQuestion: {
      psy:  { questionId: 'psy',  avgScore: 8.70, responses: 1248, deltaVsPrev: +0.05 },
      wait: { questionId: 'wait', avgScore: 8.71, responses: 1248, deltaVsPrev: -0.20 },
      cm:   { questionId: 'cm',   avgScore: 9.08, responses: 1246, deltaVsPrev: +0.05 },
      nps:  { questionId: 'nps',  avgScore: 9.11, responses: 1247, deltaVsPrev: -0.05 },
      open: { questionId: 'open', avgScore: 8.75, responses:  516, deltaVsPrev: -0.15 },
    },
  },
};

// 12-month trend per question — seeded to show the kind of pattern Shachar's
// view will surface (real query: AVG(score) GROUP BY DATE_TRUNC(day, MONTH)).
export interface MonthlyScore {
  month: string;
  psy:  number;
  wait: number;
  cm:   number;
  nps:  number;
  open: number;
}
// Mock that mirrors REAL monthly volatility from BQ (Feb + May dips).
// Real monthly aggregate AVG from BQ: Dec 9.26, Jan 8.92, Feb 8.34, Mar 8.81,
// Apr 8.70, May 8.34, Jun 8.98 (closed only). Per-question split is mocked but
// shaped to align with the aggregates.
export const SATISFACTION_TREND: MonthlyScore[] = [
  { month: 'יול׳', psy: 8.7, wait: 8.5, cm: 9.0, nps: 9.1, open: 8.6 },
  { month: 'אוג׳', psy: 8.8, wait: 8.4, cm: 9.0, nps: 9.0, open: 8.7 },
  { month: 'ספט׳', psy: 8.6, wait: 8.3, cm: 9.1, nps: 9.0, open: 8.5 },
  { month: 'אוק׳', psy: 8.7, wait: 8.5, cm: 9.1, nps: 9.1, open: 8.6 },
  { month: 'נוב׳', psy: 8.9, wait: 8.4, cm: 9.2, nps: 9.2, open: 8.7 },
  { month: 'דצמ׳', psy: 9.3, wait: 9.2, cm: 9.3, nps: 9.2, open: 9.3 }, // 9.26 agg
  { month: 'ינו׳', psy: 9.0, wait: 8.8, cm: 9.0, nps: 8.9, open: 8.7 }, // 8.92 agg
  { month: 'פבר׳', psy: 8.3, wait: 8.0, cm: 8.5, nps: 8.5, open: 7.5 }, // 8.34 agg (dip)
  { month: 'מרץ',  psy: 8.8, wait: 8.6, cm: 9.0, nps: 8.9, open: 9.3 }, // 8.81 agg
  { month: 'אפר׳', psy: 8.7, wait: 8.4, cm: 8.9, nps: 8.8, open: 8.4 }, // 8.70 agg
  { month: 'מאי',  psy: 8.3, wait: 8.0, cm: 8.5, nps: 8.5, open: 7.6 }, // 8.34 agg (dip)
  { month: 'יוני', psy: 9.0, wait: 8.8, cm: 9.0, nps: 9.0, open: 9.2 }, // 8.98 partial
];

// Per-clinic snapshot (BQ: GROUP BY Clinic + question_id).
export interface ClinicScores {
  clinic: string;
  responses: number;
  psy:  number;
  wait: number;
  cm:   number;
  nps:  number;
  open: number;
}
export const SATISFACTION_BY_CLINIC: ClinicScores[] = [
  { clinic: 'Maccabi',       responses: 612, psy: 8.6, wait: 8.4, cm: 9.0, nps: 9.0, open: 8.6 },
  { clinic: 'Maccabi Youth', responses: 318, psy: 8.9, wait: 8.5, cm: 9.2, nps: 9.2, open: 8.9 },
  { clinic: 'Clalit',        responses:  84, psy: 8.4, wait: 8.0, cm: 8.9, nps: 8.8, open: 8.5 },
  { clinic: 'Merkaz Hosen',  responses:  82, psy: 9.2, wait: 9.0, cm: 9.4, nps: 9.3, open: 9.2 },
  { clinic: 'ITC',           responses:  64, psy: 8.5, wait: 8.2, cm: 9.0, nps: 8.9, open: 8.5 },
  { clinic: 'Donation',      responses:  88, psy: 8.3, wait: 7.9, cm: 8.7, nps: 8.7, open: 8.4 },
];

// Recent open feedback (BQ: DWH.Satisfaction_OpenQuestion ordered by day DESC).
// Real ones from `v_satisfaction_feedback` sample.
export interface OpenFeedback {
  // Who was treating the person who wrote this — a comment you cannot attribute
  // is a comment nobody can act on.
  psychiatrist?: string;
  caseManager?: string;
  day: string;
  clinic: string;
  avgScore: number;
  text: string;
}
export const SATISFACTION_FEEDBACK: OpenFeedback[] = [
  { day: '2026-06-07', clinic: 'Maccabi',       avgScore: 9.8,  text: 'הכל באמת מצויין' },
  { day: '2026-06-05', clinic: 'Maccabi Youth', avgScore: 8.0,  text: 'אין לכם תאריכים מחכים האבה זמן עד שיקבע תור לפסיכיאטר' },
  { day: '2026-06-03', clinic: 'Clalit',        avgScore: 10.0, text: 'כרגע אין אפשרות להוציא סיכומים מהאתר עקב בעיית שידרוג שתיפתר, אך הוציאו לי ושלחו ועל זה כל הכבוד' },
  { day: '2026-06-03', clinic: 'Clalit',        avgScore: 8.5,  text: 'אין סיבה שבין כל פגישה לפגישה נצטרך 3 פגישות עם מנהלת טיפול...' },
  { day: '2026-06-02', clinic: 'Merkaz Hosen',  avgScore: 10.0, text: 'תודה על הליווי והאיכפתיות לסייע. הערה לשיפור — אפשרות לביטול/שינוי מועד תור באופן עצמאי לא תמיד מתאפשרת בשל בעיות טכניות.' },
];

export const SATISFACTION_TARGET = 8.5;   // Avi spec — "above 8.5 = good"
